import React, { Fragment, FC, ReactNode, useState, MouseEvent } from 'react';
import { has, get } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { createStyles, StyleRules, Theme, makeStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import {
	Box,
	Button,
	Dialog,
	DialogTitle as MuiDialogTitle,
	DialogContent as MuiDialogContent,
	DialogActions as MuiDialogActions,
	FormControl,
	Grid,
	IconButton,
	Link,
	Popover,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography
} from '@material-ui/core';
import type { ClassNameMap } from '@material-ui/styles';
import { Img } from 'react-image';

// Dialog icons
import NotificationsIcon from '@material-ui/icons/Notifications';
import CloseIcon from '@material-ui/icons/Close';
import HelpIcon from '@material-ui/icons/HelpOutlineOutlined';
import ScheduleIcon from '@material-ui/icons/Schedule';
import ClearIcon from '@material-ui/icons/BackspaceOutlined';
import AttentionIcon from '@material-ui/icons/ErrorOutline';

// Event specific icons
import GoalIcon from '@material-ui/icons/SportsSoccer';
import KickOffIcon from '@material-ui/icons/Sports';
import OwnGoalIcon from '@material-ui/icons/MoodBad';
import SecondYellowIcon from '@material-ui/icons/AccountBox';
import RedCardIcon from '@material-ui/icons/AccountBox';
import ShotIcon from '@material-ui/icons/SportsSoccer';
import MissIcon from '@material-ui/icons/CallMissedOutgoing';
import PenaltyFoulIcon from '@material-ui/icons/Sports';
import PenaltyMissIcon from '@material-ui/icons/CallMissedOutgoing';
import PenaltyGoalIcon from '@material-ui/icons/SportsSoccer';
import PenaltyShotIcon from '@material-ui/icons/SportsSoccer';

import leagueConfig from './leagueConfig.json';
import { NotificationInput, NotificationType, Team } from '../../utils/types';
import { toLocalFormattedString } from '../../utils/dateTransform';
import ConfirmDialog from '../common/Dialog';
import { listNotifications } from './apiQueries';
import { createNotification as createNotificationMutation } from './apiMutations';

const styles = (theme: Theme): StyleRules =>
	createStyles({
		root: {
			margin: 0,
			padding: theme.spacing(2)
		},
		closeButton: {
			position: 'absolute',
			right: theme.spacing(1),
			top: theme.spacing(1)
		}
	});

const useStyles = makeStyles({
	paper: {
		width: 'calc(100% - 20px)',
		maxWidth: 1024,
		maxHeight: 'calc(100% - 42px)',
		margin: '32px 10px 10px'
	},
	topScrollPaper: {
		alignItems: 'flex-start'
	},
	topPaperScrollBody: {
		verticalAlign: 'top'
	},

	confirmDialogTopScrollPaper: {
		alignItems: 'flex-start',
		marginTop: 100
	},
	confirmDialogTopPaperScrollBody: {
		verticalAlign: 'top'
	},

	helpPopover: {
		padding: '5px 10px 0'
	},

	content: {
		marginBottom: 10
	},

	eventLineIcon: {
		width: 25,
		marginRight: 8,
		verticalAlign: 'middle'
	},
	eventLineIconPad: {
		marginLeft: 8
	},

	optionalGameData: {
		width: 360,
		marginLeft: 0
	},

	buttonColumn: {
		display: 'inline-block',
		width: 78,
		verticalAlign: 'top',
		marginTop: 10,
		marginRight: 0
	},
	buttonContainer: {
		marginLeft: 8,
		display: 'inline-block',
		verticalAlign: 'top'
	},
	buttonFieldset: {
		padding: '2px 0 0 8px',
		margin: '0 0 8px 0'
	},

	eventButton: {
		display: 'inline-block',
		height: 68,
		width: 78,
		marginRight: 8,
		marginBottom: 8,
		padding: '5px 0 0',
		lineHeight: 1.4,
		fontSize: '0.85em'
	},
	eventButtonTall: {
		height: 194
	},
	buttonImg: {
		width: 30,
		marginBottom: 4
	},

	scrollingTable: {
		overflowX: 'hidden',
		overflowY: 'auto',
		maxHeight: 190,
		minHeight: 70
	},
	stickyHeader: {
		position: 'sticky',
		top: 0,
		backgroundColor: '#fff'
	},

	dt: {
		display: 'inline-block',
		width: 80
	},
	dd: {
		display: 'inline-block',
		width: 430,
		fontWeight: 'bold'
	}
});

const dialogSize = {
	minHeight: 500,
	maxHeight: 800
};

const confirmDialogSize = {
	minHeight: 400,
	maxHeight: 400
};

export interface DialogTitleProps extends WithStyles<typeof styles> {
	id: string;
	children: ReactNode;
	onClose: () => void;
}

const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
	const { children, classes, onClose, ...other } = props;
	return (
		<MuiDialogTitle disableTypography className={classes.root} {...other}>
			<Typography variant='h5'>{children}</Typography>
			{onClose ? (
				<IconButton aria-label='close' className={classes.closeButton} onClick={onClose}>
					<CloseIcon />
				</IconButton>
			) : null}
		</MuiDialogTitle>
	);
});

const DialogContent = withStyles((theme: Theme) => ({
	root: {
		padding: theme.spacing(2)
	}
}))(MuiDialogContent);

const DialogActions = withStyles((theme: Theme) => ({
	root: {
		margin: theme.spacing(0),
		padding: theme.spacing(1)
	}
}))(MuiDialogActions);

const NotificationsDialog: FC<NotificationsDialogProps> = (props) => {
	const [open, setOpen] = useState<boolean>(false);
	const [confirm, setConfirm] = useState<boolean>(false);
	const [mins, setMins] = useState<string | null>(null);
	const [scoreA, setScoreA] = useState<string | null>(null);
	const [scoreB, setScoreB] = useState<string | null>(null);
	const [pickedEvent, setPickedEvent] = useState<EventTeam | null>(null);
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

	const { gameId, startTime, league, teams, classes } = props;
	const [teamA, teamB] = teams ? (teams as Team[]) : [null, null];
	const dialogClasses = useStyles();

	const {
		loading: notificationsLoading,
		error: notificationsError,
		data: notifications,
		refetch: notificationsRefetch
	} = useQuery(listNotifications, {
		variables: { gameId },
		notifyOnNetworkStatusChange: true,
		context: { api: 'notifications' },
		fetchPolicy: 'no-cache'
	});

	const [createNotification] = useMutation(createNotificationMutation);

	const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = (): void => {
		setAnchorEl(null);
	};

	const helpOpen = Boolean(anchorEl);
	const id = helpOpen ? 'help-popover' : undefined;

	const openAction = (): void => {
		setOpen(true);
	};

	const closeAction = (event?: object, reason?: string): void => {
		if (!reason || reason !== 'backdropClick') {
			setOpen(false);
			formReset();
		}
	};

	const confirmAction = (): void => {
		setConfirm(true);
	};

	const cancelConfirmAction = (): void => {
		if (pickedEvent?.event === 'kick off' && mins === '1') {
			setMins(null);
		}
		setConfirm(false);
	};

	const formReset = (): void => {
		setMins(null);
		setScoreA(null);
		setScoreB(null);
	};

	const confirmNotificationAction = (eventTeam: EventTeam): void => {
		if (eventTeam.event === 'kick off' && (!mins || mins < '1')) {
			setMins('1');
		}

		setPickedEvent(eventTeam);
		confirmAction();
	};

	const createNotificationAction = async (): Promise<void> => {
		const id = uuidv4();
		const now = Date.now();

		const notification: NotificationInput = {
			id,
			notificationType: NotificationType.TEXT,
			timestamp: `${now}`,
			title: pickedEvent?.event!,
			minute: mins ? parseInt(mins) : undefined,
			teamId: pickedEvent?.team?.merlinId ? pickedEvent?.team.merlinId : undefined,
			score: scoreA || scoreB ? `${scoreA} - ${scoreB}` : undefined
		};

		await createNotification({
			variables: {
				gameId,
				notification
			},
			context: { api: 'notifications' }
		}).then(async (): Promise<void> => {
			setConfirm(false);
			formReset();
		});

		await notificationsRefetch();
	};

	const renderEvent = (): ReactNode => {
		return (
			<Fragment>
				<Typography variant='h6' gutterBottom>
					Current event
				</Typography>
				<div className={dialogClasses.content}>
					<Typography gutterBottom>
						<ScheduleIcon fontSize='default' className={dialogClasses.eventLineIcon} />
						{toLocalFormattedString(startTime!, false)}
					</Typography>
					<Typography gutterBottom>
						{teamA!.logoUrl && <img src={teamA!.logoUrl || ''} alt={teamA!.name || ''} className={dialogClasses.eventLineIcon} />}
						<strong>{teamA!.name}</strong> ({teamA!.abbreviation})
					</Typography>
					<Typography>
						{teamB!.logoUrl && <img src={teamB!.logoUrl || ''} alt={teamB!.name || ''} className={dialogClasses.eventLineIcon} />}
						<strong>{teamB!.name}</strong> ({teamB!.abbreviation})
					</Typography>
				</div>
			</Fragment>
		);
	};

	const renderFormControls = (): ReactNode => {
		return (
			<Fragment>
				<Typography variant='h6' gutterBottom>
					Notification actions
					<IconButton aria-describedby={id} aria-label='help' size='small' onClick={handleClick} style={{ marginLeft: 8, verticalAlign: 'top' }}>
						<HelpIcon />
					</IconButton>
				</Typography>
				<Popover
					id={id}
					open={helpOpen}
					anchorEl={anchorEl}
					onClose={handleClose}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'center'
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'center'
					}}>
					<Typography variant='h6' className={dialogClasses.helpPopover}>
						Help
					</Typography>
					<Typography variant='body2' className={dialogClasses.helpPopover}>
						To manually create <strong>text notifications</strong> click the buttons shown.
					</Typography>
					<Typography variant='body2' className={dialogClasses.helpPopover} gutterBottom>
						Optionally, please select values for game <strong>Minute</strong> and the current <strong>Scores</strong>.<br />
						Click the <strong>notification</strong> button you would like to create when happy with your entries.
					</Typography>
					<Typography variant='body2' className={dialogClasses.helpPopover} gutterBottom>
						You will be required to confirm all notifications before they are created.
					</Typography>
				</Popover>

				<Box component='fieldset' border={1} borderRadius={5} borderColor='grey.300' p={1} pl={2} className={dialogClasses.optionalGameData}>
					<legend>Optional game data</legend>
					<FormControl style={{ width: 50, marginRight: 20 }}>
						<TextField
							id='mins'
							label='Minute'
							type='number'
							InputLabelProps={{
								shrink: true
							}}
							InputProps={{ inputProps: { min: 1, max: 130 } }}
							value={mins || ''}
							onChange={(e): void => setMins(e.target.value)}
							size='small'
						/>
					</FormControl>
					<FormControl style={{ width: 90, marginRight: 5 }}>
						<TextField
							id='scoreA'
							label='Home score'
							type='number'
							InputLabelProps={{
								shrink: true
							}}
							InputProps={{ inputProps: { min: 0, max: 50 } }}
							value={scoreA || ''}
							onChange={(e): void => setScoreA(e.target.value)}
							size='small'
						/>
					</FormControl>
					<FormControl style={{ width: 90, marginRight: 20 }}>
						<TextField
							id='scoreB'
							label='Away score'
							type='number'
							InputLabelProps={{
								shrink: true
							}}
							InputProps={{ inputProps: { min: 0, max: 50 } }}
							value={scoreB || ''}
							onChange={(e): void => setScoreB(e.target.value)}
							size='small'
						/>
					</FormControl>

					<Tooltip title='Clear form'>
						<IconButton aria-label='clear' color='default' onClick={(): void => formReset()}>
							<ClearIcon />
						</IconButton>
					</Tooltip>
				</Box>
			</Fragment>
		);
	};

	const renderNotificationActions = (): ReactNode => {
		const leagueNotifications = league && has(leagueConfig, league.toLowerCase()) ? get(leagueConfig, league.toLowerCase()) : [];

		return (
			<Fragment>
				<div className={dialogClasses.content}>
					<div className={dialogClasses.buttonColumn}>
						<Button
							variant='contained'
							className={`${dialogClasses.eventButton} ${dialogClasses.eventButtonTall}`}
							onClick={(): void => confirmNotificationAction({ event: 'kick off', team: null })}>
							<KickOffIcon fontSize='large' />
							<br />
							Kick-off
						</Button>
					</div>
					<div className={dialogClasses.buttonContainer}>
						<Box component='fieldset' border={1} borderRadius={5} borderColor='grey.300' className={dialogClasses.buttonFieldset}>
							<legend>{teamA!.name}</legend>
							<Button
								variant='contained'
								className={dialogClasses.eventButton}
								onClick={(): void => confirmNotificationAction({ event: 'goal', team: teamA })}>
								{teamA!.logoUrl && (
									<Img src={teamA!.logoUrl} alt={teamA!.name!} className={dialogClasses.buttonImg} unloader={<GoalIcon fontSize='large' />} />
								)}
								{!teamA!.logoUrl && <GoalIcon fontSize='large' />}
								<br />
								Goal
							</Button>
							{leagueNotifications.map(
								({ eventName, buttonCopy, buttonIcon }: { eventName: string; buttonCopy: string; buttonIcon: string }, idx: number): ReactNode =>
									renderActionButton(eventName, 'home', idx, buttonIcon, buttonCopy)
							)}
						</Box>
						<Box component='fieldset' border={1} borderRadius={5} borderColor='grey.300' className={dialogClasses.buttonFieldset}>
							<legend>{teamB!.name}</legend>
							<Button
								variant='contained'
								className={dialogClasses.eventButton}
								onClick={(): void => confirmNotificationAction({ event: 'goal', team: teamB })}>
								{teamB!.logoUrl && (
									<Img src={teamB!.logoUrl} alt={teamB!.name!} className={dialogClasses.buttonImg} unloader={<GoalIcon fontSize='large' />} />
								)}
								{!teamB!.logoUrl && <GoalIcon fontSize='large' />}
								<br />
								Goal
							</Button>
							{leagueNotifications.map(
								({ eventName, buttonCopy, buttonIcon }: { eventName: string; buttonCopy: string; buttonIcon: string }, idx: number): ReactNode =>
									renderActionButton(eventName, 'away', idx, buttonIcon, buttonCopy)
							)}
						</Box>
					</div>
				</div>
			</Fragment>
		);
	};

	const renderActionButton = (eventName: string, currentTeam: 'home' | 'away', idx: number, buttonIcon: string, buttonCopy: string): ReactNode => {
		// 'Own goal' team inversion
		let team = teamA;
		if ((currentTeam === 'home' && eventName === 'own goal') || (currentTeam === 'away' && eventName !== 'own goal')) {
			team = teamB;
		}

		return (
			<Button
				variant='contained'
				className={dialogClasses.eventButton}
				onClick={(): void => confirmNotificationAction({ event: eventName, team })}
				key={`notificationButton_${idx}_${currentTeam}`}>
				{buttonIcon === 'owngoal' && <OwnGoalIcon fontSize='large' />}
				{buttonIcon === 'secondyellowcard' && <SecondYellowIcon fontSize='large' />}
				{buttonIcon === 'redcard' && <RedCardIcon fontSize='large' />}
				{buttonIcon === 'shot' && <ShotIcon fontSize='large' />}
				{buttonIcon === 'miss' && <MissIcon fontSize='large' />}
				{buttonIcon === 'penaltyfoul' && <PenaltyFoulIcon fontSize='large' />}
				{buttonIcon === 'penaltymiss' && <PenaltyMissIcon fontSize='large' />}
				{buttonIcon === 'penaltygoal' && <PenaltyGoalIcon fontSize='large' />}
				{buttonIcon === 'penaltyshot' && <PenaltyShotIcon fontSize='large' />}
				<br />
				{buttonCopy}
			</Button>
		);
	};

	const renderExistingNotifications = (): ReactNode => {
		let rows: Notification[] = [];
		if (!notificationsLoading && !notificationsError && notifications.getGame.notifications.items) {
			rows = notifications.getGame.notifications.items
				.filter((notification: Notification) => notification.notificationType === 'TEXT')
				.sort((a: Notification, b: Notification) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
		}

		return (
			<Fragment>
				<Typography variant='h6'>Notifications already created</Typography>
				<TableContainer className={dialogClasses.scrollingTable}>
					<Table size='small'>
						<TableHead>
							<TableRow>
								<TableCell style={{ width: 50, textAlign: 'center' }} className={dialogClasses.stickyHeader}>
									No
								</TableCell>
								<TableCell className={dialogClasses.stickyHeader}>Created</TableCell>
								<TableCell className={dialogClasses.stickyHeader}>Event</TableCell>
								<TableCell className={dialogClasses.stickyHeader}>Team logo</TableCell>
								<TableCell style={{ width: 80, textAlign: 'center' }} className={dialogClasses.stickyHeader}>
									Score
								</TableCell>
								<TableCell style={{ width: 80, textAlign: 'center' }} className={dialogClasses.stickyHeader}>
									Minute
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{notificationsLoading && (
								<TableRow>
									<TableCell colSpan={6}>Notifications loading.</TableCell>
								</TableRow>
							)}

							{notificationsError && (
								<TableRow>
									<TableCell colSpan={6}>There was an error loading existing notifications.</TableCell>
								</TableRow>
							)}

							{!notificationsLoading && !notificationsError && rows.length === 0 && (
								<TableRow>
									<TableCell colSpan={6}>No notifications found.</TableCell>
								</TableRow>
							)}

							{!notificationsLoading &&
								!notificationsError &&
								rows.map((row, idx) => (
									<TableRow key={`notification-${idx}`}>
										<TableCell component='th' scope='row' style={{ width: 50, textAlign: 'center' }}>
											{rows.length - idx}
										</TableCell>
										<TableCell>{toLocalFormattedString(row.timestamp, false, false)}</TableCell>
										<TableCell>{row.title}</TableCell>
										<TableCell>
											{row.teamId && (
												<img
													src={`https://edge.myriad-xcr.xcr.comcast.net/select/logo?width=100&height=100&extent=true&entityId=${row.teamId}`}
													alt='teamLogo'
													style={{ width: 30, verticalAlign: 'middle' }}
												/>
											)}
										</TableCell>
										<TableCell style={{ width: 80, textAlign: 'center' }}>{row.score}</TableCell>
										<TableCell style={{ width: 80, textAlign: 'center' }}>{row.minute}</TableCell>
									</TableRow>
								))}
						</TableBody>
					</Table>
				</TableContainer>
			</Fragment>
		);
	};

	const renderConfirmDialog = (): ReactNode => {
		const teamStr = pickedEvent?.team ? pickedEvent?.team.name : 'nil';
		const minsStr = mins ? mins : 'nil';
		const scoreStr = scoreA || scoreB ? `${scoreA} - ${scoreB}` : 'nil';

		return (
			<ConfirmDialog
				open={confirm}
				onCancel={cancelConfirmAction}
				onOK={createNotificationAction}
				title='Create Text Notification'
				classes={{
					scrollPaper: dialogClasses.confirmDialogTopScrollPaper,
					paperScrollBody: dialogClasses.confirmDialogTopPaperScrollBody
				}}
				style={confirmDialogSize}
				fullWidth={true}
				maxWidth='sm'
				autoFocusOk={true}>
				<Typography gutterBottom variant='body2'>
					Please confirm that would you like to create this Notification:
				</Typography>
				<Box border={1} borderRadius={5} borderColor='grey.300' p={1} pl={2} mb={2}>
					<Grid container component='dl' spacing={2}>
						<Grid item>
							<Typography component='dt' variant='body2' className={dialogClasses.dt}>
								Title
							</Typography>
							<Typography component='dd' variant='body1' className={dialogClasses.dd}>
								{pickedEvent?.event}
							</Typography>
							<Typography component='dt' variant='body2' className={dialogClasses.dt}>
								Team
							</Typography>
							<Typography component='dd' variant='body1' className={dialogClasses.dd}>
								{teamStr}
							</Typography>
							<Typography component='dt' variant='body2' className={dialogClasses.dt}>
								Minute
							</Typography>
							<Typography component='dd' variant='body1' className={dialogClasses.dd}>
								{minsStr}
							</Typography>
							<Typography component='dt' variant='body2' className={dialogClasses.dt}>
								Score
							</Typography>
							<Typography component='dd' variant='body1' className={dialogClasses.dd}>
								{scoreStr}
							</Typography>
						</Grid>
					</Grid>
				</Box>
				<Typography gutterBottom variant='body2' style={{ fontStyle: 'italic' }}>
					<AttentionIcon fontSize='small' style={{ verticalAlign: 'bottom' }} /> Manual notifications cannot be removed once created.
				</Typography>
			</ConfirmDialog>
		);
	};

	const renderNotificationsLink = (): ReactNode => {
		return (
			<Link className={classes.gameActionLink} onClick={openAction} href='#'>
				<NotificationsIcon />
				<Typography className={classes.linkText}>Notifications</Typography>
			</Link>
		);
	};

	if (!teamA || !teamB) {
		return null;
	}

	return (
		<Fragment>
			{renderNotificationsLink()}

			<Dialog
				onClose={closeAction}
				aria-labelledby='customized-dialog-title'
				open={open}
				fullWidth={true}
				maxWidth='lg'
				scroll='paper'
				classes={{
					paper: dialogClasses.paper,
					scrollPaper: dialogClasses.topScrollPaper,
					paperScrollBody: dialogClasses.topPaperScrollBody
				}}>
				<DialogTitle id='customized-dialog-title' onClose={closeAction}>
					Manual Text Notifications
				</DialogTitle>
				<DialogContent dividers style={dialogSize}>
					<Grid container spacing={1}>
						<Grid item xs={7}>
							{renderFormControls()}
						</Grid>
						<Grid item xs={5}>
							{renderEvent()}
						</Grid>
						<Grid item xs={12}>
							{renderNotificationActions()}
							{renderExistingNotifications()}
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button onClick={closeAction} color='primary'>
						Close
					</Button>
				</DialogActions>
			</Dialog>

			{renderConfirmDialog()}
		</Fragment>
	);
};

type NotificationsDialogProps = {
	gameId: string;
	startTime: string | null | undefined;
	league: string | null | undefined;
	teams: Team[] | undefined;
	classes: ClassNameMap;
};

type EventTeam = {
	event: string;
	team: Team | null;
};

type Notification = {
	id: string;
	timestamp: string;
	title: string;
	minute: string | null;
	teamId: string | null;
	score: string | null;
	notificationType: 'TEXT' | 'VIDEO';
};

export default NotificationsDialog;
